import { createFeatureSelector, createSelector } from '@ngrx/store';
import { IntegrationVendor } from '../models/integration/integration-vendor.enum';
import { INTEGRATION_FEATURE_KEY } from '../reducer/integration.feature-key';
import { IntegrationState } from '../reducer/integration.reducer';

export const selectIntegrationFeature = createFeatureSelector<IntegrationState>(INTEGRATION_FEATURE_KEY);

export const selectIsLoading = createSelector(selectIntegrationFeature, (state) => state.isLoading);
export const selectLoadError = createSelector(selectIntegrationFeature, (state) => state.loadError);

export const selectAllConnections = createSelector(selectIntegrationFeature, (state) =>
    state.connectionIds.map((id) => state.connections[id]).filter((conn) => conn != null),
);

export const selectConnectionById = (connectionId: string) =>
    createSelector(selectIntegrationFeature, (state) => state.connections[connectionId] ?? null);

export const selectConnectionsByVendor = (vendor: IntegrationVendor) =>
    createSelector(selectAllConnections, (connections) => connections.filter((conn) => conn.vendor === vendor));

export const selectFirstConnectionByVendor = (vendor: IntegrationVendor) =>
    createSelector(selectAllConnections, (connections) => connections.find((conn) => conn.vendor === vendor) ?? null);

export const selectPendingSetup = createSelector(selectIntegrationFeature, (state) => state.pendingSetup);
export const selectPendingSetupStep = createSelector(selectPendingSetup, (setup) => setup.step);
export const selectPendingSetupVendor = createSelector(selectPendingSetup, (setup) => setup.vendor);
export const selectPendingSetupError = createSelector(selectPendingSetup, (setup) => setup.error);
export const selectPendingSetupCreatedConnectionId = createSelector(
    selectPendingSetup,
    (setup) => setup.createdConnectionId,
);

// ── External companies (manage modal's BC list) ───────────────────────────

export const selectExternalCompaniesState = createSelector(
    selectIntegrationFeature,
    (state) => state.externalCompanies,
);

export const selectExternalCompaniesItems = createSelector(
    selectExternalCompaniesState,
    (ec) => ec.items,
);

export const selectIsExternalCompaniesLoading = createSelector(
    selectExternalCompaniesState,
    (ec) => ec.isLoading,
);

// ── Single edit session ───────────────────────────────────────────────────

export const selectSingleEditState = createSelector(
    selectIntegrationFeature,
    (state) => state.singleEdit,
);

/**
 * The single edit modal is shown when an active session is open.
 * Clean predicate: just reads the `isOpen` flag on a dedicated slice (no
 * disambiguation against another writer needed).
 */
export const selectSingleEditIsActive = createSelector(
    selectSingleEditState,
    (se) => se.isOpen && se.connectionId != null,
);

// ── Tenant detail (D2 use case) ───────────────────────────────────────────

export const selectTenantDetailState = createSelector(
    selectIntegrationFeature,
    (state) => state.tenantDetail,
);

export const selectTenantDetailCompanies = createSelector(
    selectTenantDetailState,
    (td) => td.aumicoCompanies,
);

export const selectIsTenantDetailLoading = createSelector(
    selectTenantDetailState,
    (td) => td.isLoading,
);

export const selectTenantDetailError = createSelector(
    selectTenantDetailState,
    (td) => td.error,
);

// ── Validate on read pattern (Section C of the BE doc) ─────────────────────
//
// Mappings are NOT cascaded when a connection is deleted/disconnected. The FE
// must cross reference per render. These helpers do that join against the
// cached connections list.

/**
 * `true` when the connection exists AND is in healthy state. A `false` here
 * means the mapping should render with a red "Re map" CTA in the UI.
 */
export const selectIsConnectionHealthyById = (connectionId: string) =>
    createSelector(selectConnectionById(connectionId), (conn) => {
        if (conn == null) return false;
        return conn.status === 'CONNECTED';
    });

// ── Auto match ────────────────────────────────────────────────────────────

export const selectAutoMatchState = createSelector(
    selectIntegrationFeature,
    (state) => state.autoMatch,
);

export const selectIsAutoMatchRunning = createSelector(
    selectAutoMatchState,
    (am) => am.isRunning,
);

/**
 * Banner state machine helper. The manage modal detail view subscribes to this
 * and renders one of three banners: running, success result, error: IFF the
 * auto match was for the current detail tenant. For any other tenant the
 * banner is hidden (returns null).
 */
// ── D365 BC journal import ────────────────────────────────────────────────

export const selectD365BcJournalImportState = createSelector(
    selectIntegrationFeature,
    (state) => state.d365bcJournalImport,
);

export const selectD365BcFiscalYears = createSelector(
    selectD365BcJournalImportState,
    (s) => s.fiscalYears,
);

export const selectD365BcFiscalYearsLoading = createSelector(
    selectD365BcJournalImportState,
    (s) => s.fiscalYearsLoading,
);

export const selectD365BcImportStatus = createSelector(
    selectD365BcJournalImportState,
    (s) => s.importStatus,
);

export const selectD365BcImportResult = createSelector(
    selectD365BcJournalImportState,
    (s) => s.importResult,
);

export const selectD365BcImportError = createSelector(
    selectD365BcJournalImportState,
    (s) => s.importError,
);

// ── Abacus journal import ──────────────────────────────────────────────────

export const selectAbacusJournalImportState = createSelector(
    selectIntegrationFeature,
    (state) => state.abacusJournalImport,
);

export const selectAbacusImportStatus = createSelector(
    selectAbacusJournalImportState,
    (s) => s.importStatus,
);

export const selectAbacusImportResult = createSelector(
    selectAbacusJournalImportState,
    (s) => s.importResult,
);

export const selectAbacusImportError = createSelector(
    selectAbacusJournalImportState,
    (s) => s.importError,
);

// ── Topal journal import ───────────────────────────────────────────────────

export const selectTopalJournalImportState = createSelector(
    selectIntegrationFeature,
    (state) => state.topalJournalImport,
);

export const selectTopalImportStatus = createSelector(
    selectTopalJournalImportState,
    (s) => s.importStatus,
);

export const selectTopalImportResult = createSelector(
    selectTopalJournalImportState,
    (s) => s.importResult,
);

export const selectTopalImportError = createSelector(
    selectTopalJournalImportState,
    (s) => s.importError,
);

// ── Accounto journal import ────────────────────────────────────────────────

export const selectAccountoJournalImportState = createSelector(
    selectIntegrationFeature,
    (state) => state.accountoJournalImport,
);

export const selectAccountoImportStatus = createSelector(
    selectAccountoJournalImportState,
    (s) => s.importStatus,
);

export const selectAccountoImportResult = createSelector(
    selectAccountoJournalImportState,
    (s) => s.importResult,
);

export const selectAccountoImportError = createSelector(
    selectAccountoJournalImportState,
    (s) => s.importError,
);

// ── Proffix journal import ─────────────────────────────────────────────────

export const selectProffixJournalImportState = createSelector(
    selectIntegrationFeature,
    (state) => state.proffixJournalImport,
);

export const selectProffixImportStatus = createSelector(
    selectProffixJournalImportState,
    (s) => s.importStatus,
);

export const selectProffixImportResult = createSelector(
    selectProffixJournalImportState,
    (s) => s.importResult,
);

export const selectProffixImportError = createSelector(
    selectProffixJournalImportState,
    (s) => s.importError,
);

// ── SharePoint document source (REV-21679) ─────────────────────────────────

export const selectSharepointState = createSelector(
    selectIntegrationFeature,
    (state) => state.sharepoint,
);

export const selectSharepointConnection = createSelector(
    selectSharepointState,
    (sp) => sp.connection,
);

export const selectSharepointFolders = createSelector(
    selectSharepointState,
    (sp) => sp.folders,
);

export const selectSharepointLoading = createSelector(
    selectSharepointState,
    (sp) => sp.isLoading,
);

export const selectSharepointError = createSelector(
    selectSharepointState,
    (sp) => sp.error ?? null,
);

export const selectSharepointPickerToken = createSelector(
    selectSharepointState,
    (sp) => sp.pickerToken ?? null,
);

/** Tenant SharePoint Online origin the File Picker must be opened against (null = unknown). */
export const selectSharepointPickerBaseUrl = createSelector(
    selectSharepointState,
    (sp) => sp.pickerBaseUrl ?? null,
);

/**
 * TERMINAL picker-token failure (null = none). The container needs it to tell "not there
 * yet" from "never coming", so it can keep the pre-opened popup alive while waiting and
 * only close it on a real failure (REV-21057 BUG 2).
 */
export const selectSharepointPickerTokenError = createSelector(
    selectSharepointState,
    (sp) => sp.pickerTokenError ?? null,
);

export const selectAutoMatchBannerForConnection = (connectionId: string) =>
    createSelector(selectAutoMatchState, (am) => {
        if (am.connectionId !== connectionId) return null;
        if (am.isRunning) {
            return { kind: 'running' as const };
        }
        if (am.error != null) {
            return { kind: 'error' as const, message: am.error };
        }
        if (am.lastResult != null) {
            return {
                kind: 'success' as const,
                autoMatched: am.lastResult.autoMatched,
                notMapped: am.lastResult.notMapped,
                errors: am.lastResult.errors,
            };
        }
        return null;
    });
